import { createSwiper } from '@nulo/swiper';
import { whenNearViewport } from '@nulo/a11y';

/**
 * @param {string | undefined} value
 * @returns {number | null}
 */
function parseDestroyAbove(value) {
  const parsed = Number.parseInt(value ?? '', 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

/**
 * @extends {HTMLElement}
 */
class NuloBenefitList extends HTMLElement {
  /** @type {import('swiper').Swiper | null} */
  #swiper = null;

  /** @type {MediaQueryList | null} */
  #desktopMq = null;

  /** @type {(() => void) | null} */
  #mqListener = null;

  connectedCallback() {
    this.#prepareNearViewport();
  }

  disconnectedCallback() {
    this.#destroySwiper();
    if (this.#mqListener && this.#desktopMq) {
      this.#desktopMq.removeEventListener('change', this.#mqListener);
      this.#mqListener = null;
      this.#desktopMq = null;
    }
  }

  async #prepareNearViewport() {
    await whenNearViewport(this);
    if (!this.isConnected) return;

    const destroyAbove = parseDestroyAbove(this.dataset.destroyAbove);

    if (destroyAbove) {
      this.#desktopMq = window.matchMedia(`(min-width: ${destroyAbove}px)`);
      this.#mqListener = () => this.#syncSwiper();
      this.#desktopMq.addEventListener('change', this.#mqListener);
      this.#syncSwiper();
      return;
    }

    this.#initSwiper(this.dataset.swiperMode || 'default');
  }

  #syncSwiper() {
    if (this.#desktopMq?.matches) {
      this.#destroySwiper();
      return;
    }

    this.#initSwiper(this.dataset.swiperMode || 'default');
  }

  /**
   * @param {string} mode
   */
  #initSwiper(mode) {
    if (this.#swiper) return;

    const region = this.querySelector('[data-nulo-benefit-carousel]');
    const swiperEl = region?.querySelector('[data-swiper]');

    if (!(region instanceof HTMLElement) || !(swiperEl instanceof HTMLElement)) return;

    /** @type {import('swiper').SwiperOptions} */
    const config = {
      slidesPerView: 1,
      spaceBetween: 0,
      navigation: {
        nextEl: region.querySelector('.nulo-benefit-list__nav--next'),
        prevEl: region.querySelector('.nulo-benefit-list__nav--prev'),
      },
    };

    if (mode === 'tablet-2') {
      config.breakpoints = {
        768: {
          slidesPerView: 2,
          spaceBetween: 0,
        },
      };
    }

    this.#swiper = createSwiper({
      region,
      el: swiperEl,
      activeSlideClass: 'swiper-slide-fully-visible',
      config,
    });
  }

  #destroySwiper() {
    this.#swiper?.destroy?.(true, true);
    this.#swiper = null;
  }
}

if (!customElements.get('nulo-benefit-list')) {
  customElements.define('nulo-benefit-list', NuloBenefitList);
}
